export const THEMES = [
  {
    id: 'green',
    name: '森林绿',
    primary: '#337537',
    dark: '#2A602E',
    darker: '#1C4A1F',
    light: 'rgba(51,117,55,0.08)',
    tagBg: '#E8F5E9',
    tagBorder: '#C8E6C9',
  },
  {
    id: 'blue',
    name: '海洋蓝',
    primary: '#1565C0',
    dark: '#0D47A1',
    darker: '#0A3370',
    light: 'rgba(21,101,192,0.08)',
    tagBg: '#E3F2FD',
    tagBorder: '#BBDEFB',
  },
  {
    id: 'purple',
    name: '暮色紫',
    primary: '#6A1B9A',
    dark: '#4A148C',
    darker: '#38006B',
    light: 'rgba(106,27,154,0.08)',
    tagBg: '#F3E5F5',
    tagBorder: '#E1BEE7',
  },
  {
    id: 'orange',
    name: '烈焰橙',
    primary: '#D84315',
    dark: '#BF360C',
    darker: '#8B1A00',
    light: 'rgba(216,67,21,0.08)',
    tagBg: '#FFF3E0',
    tagBorder: '#FFCCBC',
  },
  {
    id: 'teal',
    name: '青竹绿',
    primary: '#00796B',
    dark: '#00695C',
    darker: '#004D40',
    light: 'rgba(0,121,107,0.08)',
    tagBg: '#E0F2F1',
    tagBorder: '#B2DFDB',
  },
]

// ── 颜色计算工具 ──────────────────────────────────────
function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h, s
  const l = (max + min) / 2
  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4
    }
    h /= 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

function hslToHex(h, s, l) {
  h /= 360; s /= 100; l /= 100
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let r, g, b
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  return '#' + [r, g, b].map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('')
}

function adjustL(hex, delta) {
  const { r, g, b } = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(r, g, b)
  return hslToHex(h, s, Math.min(100, Math.max(0, l + delta)))
}

function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r},${g},${b},${alpha})`
}

// ── 应用主题色对象到 CSS 变量 ──────────────────────────
function applyVars(theme) {
  const root = document.documentElement
  root.style.setProperty('--primary', theme.primary)
  root.style.setProperty('--primary-dark', theme.dark)
  root.style.setProperty('--primary-darker', theme.darker)
  root.style.setProperty('--primary-light', theme.light)
  root.style.setProperty('--primary-tag-bg', theme.tagBg)
  root.style.setProperty('--primary-tag-border', theme.tagBorder)
  root.style.setProperty('--primary-selected', hexToRgba(theme.primary, 0.15))
  root.style.setProperty('--sidebar-gradient', `linear-gradient(180deg, ${theme.darker} 0%, ${theme.primary} 100%)`)
}

// ── 应用预设主题 ──────────────────────────────────────
export function applyTheme(themeId) {
  const theme = THEMES.find(t => t.id === themeId) || THEMES[0]
  applyVars(theme)
  localStorage.setItem('theme', themeId)
}

// ── 应用自定义颜色（从单个 hex 推导所有变量）────────────
export function applyCustomColor(hex) {
  const theme = {
    primary: hex,
    dark:    adjustL(hex, -12),
    darker:  adjustL(hex, -25),
    light:   hexToRgba(hex, 0.08),
    tagBg:   adjustL(hex, 42),
    tagBorder: adjustL(hex, 28),
  }
  applyVars(theme)
  localStorage.setItem('theme', `custom:${hex}`)
}

// ── 初始化（读取 localStorage 并恢复主题）────────────────
export function initTheme() {
  const saved = localStorage.getItem('theme') || 'green'
  if (saved.startsWith('custom:')) {
    applyCustomColor(saved.slice(7))
  } else {
    applyTheme(saved)
  }
  return saved
}
